import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, User as UserIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * ProfileCompletionModal — prompted on app open if the current user was
 * created before the Feb 2026 mandatory-fields rollout (Google auth /
 * legacy signups) and is still missing any of: first_name, last_name,
 * phone, address, cap, city.
 *
 * The modal is non-dismissable (no close button, no backdrop close)
 * because the fields are legally required under the GDPR consent recorded
 * during signup — the app should not proceed without them.
 */
const REQUIRED = ["first_name", "last_name", "phone", "address", "cap", "city"];

const ProfileCompletionModal = () => {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", address: "", cap: "", city: "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) { setOpen(false); return; }
    // Only show if ANY required field is empty. Admin bypass (admins can be
    // pre-existing before the migration and are allowed to skip).
    const missing = REQUIRED.some((k) => !(user[k] && String(user[k]).trim()));
    setOpen(missing && user.role !== "admin");
    if (missing) {
      setForm({
        first_name: user.first_name || "",
        last_name:  user.last_name  || "",
        phone:      user.phone      || "",
        address:    user.address    || "",
        cap:        user.cap        || "",
        city:       user.city       || "",
      });
    }
  }, [user]);

  const submit = async () => {
    for (const k of REQUIRED) {
      if (!form[k].trim()) { toast.error(`Campo obbligatorio: ${k}`); return; }
    }
    if (form.phone.trim().length < 6) { toast.error("Cellulare troppo corto"); return; }
    setBusy(true);
    try {
      const r = await api.put("/users/me/profile", form);
      setUser({ ...user, ...r.data });
      toast.success("Profilo completato — grazie!");
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  if (!user || !open) return null;
  return (
    <Dialog open={open} onOpenChange={() => { /* non-dismissable */ }}>
      <DialogContent
        className="max-w-lg rounded-2xl p-0 overflow-hidden"
        data-testid="profile-completion-modal"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="bg-gradient-to-br from-[#12466F] via-[#0F5A89] to-[#0A84FF] text-white px-6 py-6">
          <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center mb-3">
            <UserIcon className="w-5 h-5" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] opacity-70 font-semibold">Completa il profilo</div>
          <DialogTitle className="font-display font-black text-2xl mt-1 text-white">Prima di continuare</DialogTitle>
          <p className="text-white/80 text-sm mt-2 leading-relaxed">Per rispettare i requisiti di registrazione GDPR ti chiediamo di completare i tuoi dati personali. Ci vuole meno di un minuto.</p>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Nome *</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-xl mt-1 bg-slate-50" data-testid="pc-first-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Cognome *</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-xl mt-1 bg-slate-50" data-testid="pc-last-name" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Cellulare *</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+39 333 1234567" className="rounded-xl mt-1 bg-slate-50" data-testid="pc-phone" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Indirizzo *</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Via Roma 12" className="rounded-xl mt-1 bg-slate-50" data-testid="pc-address" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">CAP *</Label>
              <Input value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} className="rounded-xl mt-1 bg-slate-50 font-mono-num" data-testid="pc-cap" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Città *</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-xl mt-1 bg-slate-50" data-testid="pc-city" />
            </div>
          </div>
        </div>
        <DialogFooter className="p-5 pt-0">
          <Button onClick={submit} disabled={busy} className="w-full rounded-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold" data-testid="pc-submit">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvo…</> : "Salva e continua"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileCompletionModal;
